import React from 'react';
import type { ComponentConfig } from '@puckeditor/core';
import styled from 'styled-components';

import { mediaQueries } from 'src/theme/constants/media';

import { lookup } from '../fields/tokens';
import type { BlockSpacing } from '../fields/tokens';

/** `same` keeps the desktop height on mobile rather than overriding it. */
export const MOBILE_INHERIT = 'same';

export type MobileHeight = BlockSpacing | typeof MOBILE_INHERIT;

export type SpacerProps = {
  height: BlockSpacing;
  mobileHeight: MobileHeight;
};

export type SpacerRenderProps = SpacerProps & {
  puck?: {
    isEditing?: boolean;
  };
};

const transientProps = new Set(['isEditing']);

/**
 * Spacer-specific workshop rhythm; the shared layout token values intentionally remain unchanged.
 *
 * Each step keeps the shipped theming hook: a Spacer-scoped `--dcm-cms-spacer-*` variable wins,
 * then the shared `--dcm-cms-space-*` variable that Spacer has always honored, then the workshop
 * value. Themes that already tune vertical rhythm through `--dcm-cms-space-*` keep working.
 */
export const SPACER_HEIGHTS: Record<BlockSpacing, string> = {
  xs: 'var(--dcm-cms-spacer-xs, var(--dcm-cms-space-xs, 8px))',
  sm: 'var(--dcm-cms-spacer-sm, var(--dcm-cms-space-sm, 16px))',
  md: 'var(--dcm-cms-spacer-md, var(--dcm-cms-space-md, 32px))',
  lg: 'var(--dcm-cms-spacer-lg, var(--dcm-cms-space-lg, 48px))',
  xl: 'var(--dcm-cms-spacer-xl, var(--dcm-cms-space-xl, 64px))',
  xxl: 'var(--dcm-cms-spacer-2xl, var(--dcm-cms-space-2xl, 96px))',
};

function resolveSpacerHeight(value: unknown): string {
  return lookup(SPACER_HEIGHTS, typeof value === 'string' ? value : '', SPACER_HEIGHTS.lg);
}

/**
 * The mobile override falls back to the desktop variable, so leaving mobile on `same`
 * means no second value to keep in sync.
 *
 * In editor mode (`isEditing: true`), a minimum height and outline affordance ensures
 * the spacer remains selectable and draggable even at small preset steps (e.g. `xs` 8px).
 * On the storefront (`isEditing: false`), no editor-only affordance styles are emitted.
 */
const StyledSpacer = styled.div.withConfig({
  shouldForwardProp: (prop) => !transientProps.has(prop),
})<{ isEditing?: boolean }>`
  height: var(--cms-spacer-height, ${SPACER_HEIGHTS.lg});

  ${({ isEditing }) =>
    isEditing &&
    `
    min-height: 16px;
    outline: 1px dashed var(--dcm-cms-spacer-editor-outline, rgba(0, 0, 0, 0.15));
    outline-offset: -1px;
  `}

  @media ${mediaQueries.largePhone} {
    height: var(--cms-spacer-mobile-height, var(--cms-spacer-height, ${SPACER_HEIGHTS.lg}));
  }
`;

export const HEIGHT_OPTIONS: { label: string; value: BlockSpacing }[] = [
  { label: 'Large (48px)', value: 'lg' },
  { label: 'Extra small (8px)', value: 'xs' },
  { label: 'Small (16px)', value: 'sm' },
  { label: 'Medium (32px)', value: 'md' },
  { label: 'Extra large (64px)', value: 'xl' },
  { label: '2X large (96px)', value: 'xxl' },
];

export const MOBILE_HEIGHT_OPTIONS: { label: string; value: MobileHeight }[] = [
  { label: 'Same as desktop', value: MOBILE_INHERIT },
  { label: 'Extra small (8px)', value: 'xs' },
  { label: 'Small (16px)', value: 'sm' },
  { label: 'Medium (32px)', value: 'md' },
  { label: 'Large (48px)', value: 'lg' },
];

export const spacerConfig: ComponentConfig<SpacerProps> = {
  label: 'Spacer',
  ai: {
    instructions:
      'Invisible vertical space between components. Use to create breathing room between sections. Prefer a scale step over stacking multiple spacers.',
  },
  fields: {
    height: {
      type: 'select',
      label: 'Desktop height',
      options: HEIGHT_OPTIONS,
      ai: {
        schema: { type: 'string', enum: HEIGHT_OPTIONS.map(({ value }) => value) },
        instructions: "Vertical space from the shared rhythm scale. 'lg' suits gaps between page sections.",
      },
    },
    mobileHeight: {
      type: 'select',
      label: 'Mobile height',
      options: MOBILE_HEIGHT_OPTIONS,
      ai: {
        schema: { type: 'string', enum: MOBILE_HEIGHT_OPTIONS.map(({ value }) => value) },
        instructions:
          "Optional height on phones. Leave as 'same' to preserve the desktop gap, or choose a workshop step for phones.",
      },
    },
  },
  defaultProps: {
    height: 'lg',
    mobileHeight: MOBILE_INHERIT,
  },
  render: ({ height = 'lg', mobileHeight = MOBILE_INHERIT, puck }: SpacerRenderProps) => {
    const isEditing = Boolean(puck?.isEditing);
    const style: Record<string, string> = {
      '--cms-spacer-height': resolveSpacerHeight(height),
    };

    if (mobileHeight !== MOBILE_INHERIT) {
      style['--cms-spacer-mobile-height'] = resolveSpacerHeight(mobileHeight);
    }

    return (
      <StyledSpacer
        data-testid='cms-spacer'
        isEditing={isEditing}
        aria-hidden='true'
        style={style as React.CSSProperties}
      />
    );
  },
};
